<template>
  <div class="client-edit">
    <div class="lead-edit-container" ref="leadEditWrapper">
      <div class="lead-edit-h" v-if="client">
        <div class="lead-edit-title">
          <span class="text">{{ $t('Leads.LeadEditPageTitle', { name: client.FullName }) }}</span>
        </div>
        <div class="lead-edit-close" @click="emitToggleClientEdit2">
          <icon-base width="16" height="16" view-box="0 0 7 7" icon-name="times"><icon-times /></icon-base>
        </div>
      </div>
      <div class="lead-edit-b" v-if="client">
        <div class="lead-form">
          <div class="lead-field full-width-field" v-if="clientEditViewModel.ShowFullname">
            <b-field :label="$t('Leads.Grid.Columns.FullName')">
              <b-input type="textarea" :value="client.FullName" disabled></b-input>
            </b-field>
          </div>
          <div class="lead-field full-width-field" v-if="clientEditViewModel.ShowEmail">
            <b-field :label="$t('Leads.Grid.Columns.Email')">
              <b-input type="textarea" :value="client.Email" disabled></b-input>
            </b-field>
          </div>
          <div class="lead-field full-width-field" v-if="clientEditViewModel.ShowPhone">
            <b-field :label="$t('Leads.Grid.Columns.Phone')">
              <b-input type="textarea" :value="client.Phone" dir="ltr" disabled></b-input>
            </b-field>
          </div>
          <div class="lead-field full-width-field">
            <b-field :label="$t('Leads.Grid.Columns.Address')">
              <b-input :value="client.Address" disabled></b-input>
            </b-field>
          </div>
          <div class="lead-field full-width-field" v-if="clientEditViewModel.ShowOtherData">
            <b-field :label="$t('Leads.Grid.Columns.Data')">
              <b-input type="textarea" :value="client.Data" disabled></b-input>
            </b-field>
          </div>
          <div class="notes">
            <p class="label">{{ $t("Leads.CrmNotes") }}</p>
            <b-collapse v-for="(note, index) of filteredNotes" :key="leadNoteStableKey(note)" :open="false" class="card" animation="slide" :aria-id="'contentIdForA11y5-' + index">
              <template #trigger="props">
                <div class="card-header" role="button" :aria-controls="'contentIdForA11y5-' + index">
                  <p class="card-header-title">
                    <span>{{ formatDate(note.CreatedDate) }}</span>
                    <span v-if="note.UserName" class="has-text-grey is-size-7"> — {{ note.UserName }}</span>
                    <span v-else-if="note.LeadId != null && note.LeadId > 0" class="has-text-grey is-size-7"> — {{ $t("Leads.NoteFromLead", { id: note.LeadId }) }}</span>
                  </p>
                  <a class="card-header-icon">
                    <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
                  </a>
                </div>
              </template>
              <div class="card-content">
                <div class="content note-text">{{ note.Note }}</div>
              </div>
            </b-collapse>
            <div v-if="!filteredNotes.length" class="notes-empty has-text-grey">{{ $t("Leads.CrmNotesEmpty") }}</div>
          </div>
        </div>
      </div>
    </div>
    <div class="lead-edit-bg" @click="emitToggleClientEdit2"></div>
  </div>
</template>

<script lang="ts">
import moment from "moment";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { Lead, ClientEditViewModel, UpdateLeadResponse } from "@/models/leads/Lead";
import HelperService from "@/services/HelperService";
import IconBase from "../IconBase.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";
import IconTimes from "../icons/IconTimes.vue";
import ApiService, { AssigneesForClientPayload } from "@/services/api/ApiService";
import { LeadNote, leadNoteStableKey } from "@/models/leads/LeadNote";
import { Assignee } from "@/models/MultiselectItem";
import i18n from "@/i18n";

const namespace = "leadsModule";

@Component({
  components: {
    IconBase,
    IconCaretDown,
    IconTimes,
  },
})
export default class ClientsGridContentEdit extends Vue {
  callForwardingNumber = "";
  emailForReferrals = "";
  mobileForSMS = "";
  numberDisplayed = "";

  leadNoteStableKey = leadNoteStableKey;

  emitToggleClientEdit2(): void {
    this.$emit("toggle-client-edit2");
  }


  //#region Vuex getters and actions

  @Getter("clientEditViewModel", { namespace }) clientEditViewModel!: ClientEditViewModel;
  @Getter("selectedClient", { namespace }) selectedClient!: Lead;
  @Action("getAssigneesForClient", { namespace }) getAssigneesForClient!: (payload: AssigneesForClientPayload) => Promise<Assignee[]>;
  @Action("getClientEditViewModel", { namespace }) getClientEditViewModel!: (id: number) => Promise<ClientEditViewModel>;
  @Action("setClientEditViewModel", { namespace }) setClientEditViewModel!: (clientEditViewModel: ClientEditViewModel) => Promise<void>;
  
  //#endregion Vuex getters and actions


  //#region Watchers

  @Watch("clientEditViewModel", { immediate: true })
  onClientEditViewModelChange(newClientEditViewModel: ClientEditViewModel, oldClientEditViewModel: ClientEditViewModel): void {
    if (!newClientEditViewModel) {
      return;
    }
    this.getAssigneesForClient({
      assigneeId: newClientEditViewModel.Client.UserId,
      companyId: newClientEditViewModel.Client.CompanyId
    });
  }

  //#endregion Watchers


  //#region Getters

  get filteredNotes(): LeadNote[] {
    return this.clientEditViewModel.LeadNotes.filter((i: LeadNote) => !i.Note.includes("עודכן על ידי Fixdigital"));
  }

  get client(): Lead {
    return this.clientEditViewModel?.Client;
  }

  //#endregion Getters


  //#region Lifecycle

  mounted(): void {
    let loader = this.$loading.show({
      container: this.$refs.leadEditWrapper
    });
    this.getClientEditViewModel(this.selectedClient.Id).then(() => {
      loader.hide();
    });
  }

  //#endregion Lifecycle


  //#region Handlers

  save() {
    this.setClientEditViewModel(this.clientEditViewModel);
    if (!this.clientEditViewModel.EditAllLeads) {
      HelperService.showError(i18n.t("NotAllowed").toString());
      return;
    }
    let loader = this.$loading.show({
      container: this.$refs.leadEditWrapper
    });
    ApiService.updateClient(this.clientEditViewModel.Client).then((response: UpdateLeadResponse) => {
      if (response.success) {
        HelperService.showNotification(response.message, "is-success", "is-top-right", 5000);
      } else {
        HelperService.showError(response.message);
      }
      loader.hide();
    })
  }

  //#endregion Handlers


  //#region Private methods

  formatDate(date: Date): string {
    return moment(date).format("DD.MM.YYYY HH:mm");
  }

  //#endregion Private methods
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.client-edit {
  position: fixed;
  z-index: 101;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;

  .lead-edit-container {
    position: fixed;
    top: 0;
    right: 0;
    width: 477px;
    height: 100%;
    max-height: 100%;
    z-index: 10;
    transition: all 0.5s;
    transform: translateX(0);
    background: #f6f7fb;

    @media (max-width: 991px) {
      // width: 100%;
      height: 100%;
    }

    @media (max-width: 575px) {
      width: 100%;
    }
  }

  .lead-edit-h {
    position: fixed;
    top: 0;
    left: 0;
    height: 56px;
    width: 100%;
    color: #fff;
    display: flex;
    align-items: center;
    padding: 12px 15px 13px 10px;
    background: linear-gradient(90deg, $primary 0%, #1ca8d9 52.75%);

    .lead-edit-close {
      padding: 5px;
      cursor: pointer;
      line-height: 0;
      margin: 0 0 0 auto;
      transition: all 0.2s;

      &:hover {
        opacity: 0.8;
      }

      .icon-w {
        svg {
        }
      }
    }
    .lead-edit-title {
      display: flex;
      align-items: center;
      font-size: 21px;
      .icon-w {
        margin-left: 10px;
      }
    }
  }

  .lead-edit-b {
    position: fixed;
    top: 56px;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 22px 1.5rem 40px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: stretch;

    @media (max-width: 575px) {
      padding: 10px 15px 30px;
    }

    hr {
      border-bottom: 1px solid #e0e2eb;
      grid-column: 1 / 3;
      margin: 0 0 1rem;
    }

    .lead-edit-switches {
      padding: 20px 0;
      border-bottom: 1px solid $lightd;
      .switch {
        width: 100%;
        justify-content: space-between;
      }
    }

    .textarea {
      height: 59px;
    }

    .dropdown {
      width: 100%;
      .dropdown-trigger {
        .button {
          width: 100%;
        }
      }
    }
    
    .buttons {
      margin: auto -10px 0;
      display: flex;
      padding: 0;
      @media (max-width: 575px) {
        margin: auto -8px 0;
      }
      .button-w {
        width: 50%;
        flex: 0 0 50%;
        padding: 0 10px;
        @media (max-width: 575px) {
          padding: 0 8px;
        }
      }
      .button {
        margin: 0;
        min-width: 0;
      }
    }

    .lead-form {
      display: grid;
      grid-gap: 1rem;
      grid-template-columns: repeat(auto-fill, 45%);
      justify-content: space-between;

      @media (max-width: 991px) {
        display: block;
      }

      .lead-field {
        margin-bottom: 0;

        &.full-width-field {
          grid-column: 1 / 3;
        }

        input[disabled], select[disabled], textarea[disabled] {
          background: #eff0f6;
        }
      }

      .notes {
        grid-column: 1/3;
        width: 100%;

        .note-text {
          white-space: pre-line;
        }
      }
    }

    .toolbar {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;

      @media (max-width: 991px) {
        margin-bottom: 1rem;
      }

      .toolbar-item {
        align-items: center;
        display: flex;
        margin-bottom: 1rem;
        width: 45%;

        @media (max-width: 991px) {
          margin: 0.2rem 0 0;
          width: 100%;
        }

        .button {
          width: 100%;

          .button-inner {
            display: flex;

            i {
              margin-inline-end: .5rem;
            }
          }
        }
      }
    }
  }
  .lead-edit-bg {
    position: fixed;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    z-index: 9;
    transition: all 0.5s;
    background: rgba(0, 0, 0, 0.3);
  }
  .buttons {
    padding-bottom: 30px;
    .button {
      margin: 0;
    }
  }

  &.fade-enter-active,
  &.fade-leave-active {
    z-index: 101;
    transition: all 0.5s;
  }
  &.fade-enter,
  &.fade-leave-to,
  &.fade-leave-active {
    opacity: 1;
    .lead-edit-container {
      transform: translateX(100%);
    }
    .lead-edit-bg {
      opacity: 0;
    }
  }
  &.fade-enter {
  }
  &.active {
    z-index: 101;
    .lead-edit-container {
      // transform: translateX(0);
    }
  }
}

html[dir="rtl"] {
  .lead-edit {
    .lead-edit-b {
      .toolbar {
        .toolbar-item {
          .button {
            .button-inner {
              i {
                margin-inline-end: 0;
                margin-inline-start: .5rem;
              }
            }
          }
        }
      }
    }

    .lead-edit-h {
      padding: 12px 15px 13px 10px;

      .lead-edit-close {
        margin: 0 auto 0 0;
      }
    }
  }
}

@media (resolution: 120dpi) {
  .lead-edit .lead-edit-container {
    height: 125%;
    max-height: 125%;
  }
  .lead-edit .lead-edit-bg {
    height: 125%;
  }
}
</style>
